'use client'

import { Button } from '@heroui/button'
import { Modal, ModalBody, ModalContent } from '@heroui/modal'
import { Skeleton } from '@heroui/skeleton'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { CrossIcon, FireIcon } from '@/components/shared/icons'
import { Typography } from '@/components/ui'
import { useApi } from '@/trpc/react'
import { cn } from '@/utils'

import { WeekStreak } from '..'




interface StreakModalProps {
  trigger?: React.ReactNode
}

const StreakModal = ({ trigger }: StreakModalProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const api = useApi()
  const {
    data: streakResponse,
    isLoading: isStreakLoading,
    isPending: isStreakPending,
  } = useSuspenseQuery(api.streaks.getStreak.queryOptions())

  const isLoading = isStreakLoading || isStreakPending
  const streak = streakResponse?.currentStreak ?? 0

  return (
    <>
      <span onClick={() => setIsOpen(true)} className="cursor-pointer">
        {trigger}
      </span>
      <Modal
        placement="center"
        size="sm"
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        hideCloseButton
        classNames={{
          base: 'mx-6',
          body: 'items-center gap-4 py-8 px-6',
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalBody>
                <div className="flex flex-col items-center">
                  <FireIcon
                    className={cn(
                      'mb-1 text-[56px]',
                      streak > 0 ? 'text-secondary' : 'text-default-300',
                    )}
                  />
                  <Typography size="2xlarge" className="font-crimsonPro">
                    Streak
                  </Typography>
                  {isLoading ? (
                    <Skeleton className="h-10 w-20" />
                  ) : (
                    <Typography
                      size="small"
                      className="text-content4-foreground"
                    >
                      {streak} day{streak > 1 ? 's' : ''}
                    </Typography>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <WeekStreak />
                  <Typography
                    size="small"
                    className="text-center font-crimsonPro text-content4-foreground/50"
                  >
                    This week
                  </Typography>
                </div>
                <div className="flex flex-col gap-4">
                  <Typography
                    size="xsmall"
                    className="text-center text-content4-foreground"
                  >
                    We want journaling to become a meaningful and enjoyable
                    habit in your life, not a mandatory task. That’s why we’ve
                    designed our streaks with a flexible, daily-ish approach.
                  </Typography>
                  <Typography
                    size="xsmall"
                    className="text-center text-content4-foreground"
                  >
                    You can take up to 2 days off without breaking your streak.
                    Journaling 3-4 times per week is a great commitment that can
                    lead to personal growth without adding unnecessary pressure.
                  </Typography>
                </div>
                <Button
                  variant="solid"
                  color="default"
                  radius="full"
                  isIconOnly
                  className="absolute right-4 top-4 bg-default-100 text-2xl"
                  onPress={onClose}
                >
                  <CrossIcon />
                </Button>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}

export default StreakModal
